import {
  connectBaseApi,
  createRpcQuery,
  createRpcMutation,
  govDashClient,
  type ConnectProto,
} from "@racoon/api";

type DriverOnShiftsArg = Parameters<typeof govDashClient.driverOnShifts>[0];
type DriverOnShiftList = Awaited<ReturnType<typeof govDashClient.driverOnShifts>>;

// Backend caps ListFilter.page_size at 100.
const DRIVER_ON_SHIFTS_PAGE_SIZE = 100;

const govDashApi = connectBaseApi.injectEndpoints({
  endpoints: (build) => ({
    anomalieRoute: createRpcQuery(build, govDashClient, "anomalieRoute"),
    anomaliesList: createRpcQuery(build, govDashClient, "anomaliesList"),
    containerTypes: createRpcQuery(build, govDashClient, "containerTypes"),
    depots: createRpcQuery(build, govDashClient, "depots", {
      providesTags: ["Depot"],
    }),
    disposalSites: createRpcQuery(build, govDashClient, "disposalSites", {
      providesTags: ["DisposalSite"],
    }),
    disposalSiteLocations: createRpcQuery(build, govDashClient, "disposalSiteLocations", {
      providesTags: ["DisposalSite"],
    }),
    getGovGeoAccess: createRpcQuery(build, govDashClient, "getGovGeoAccess", {
      providesTags: ["GovGeoAccess"],
    }),
    pickupLocations: createRpcQuery(build, govDashClient, "pickupLocations", {
      providesTags: ["PickupLocation"],
    }),
    serviceProviders: createRpcQuery(build, govDashClient, "serviceProviders", {
      providesTags: ["ServiceProvider"],
    }),
    serviceReceivers: createRpcQuery(build, govDashClient, "serviceReceivers", {
      providesTags: ["ServiceReceiver"],
    }),
    trucks: createRpcQuery(build, govDashClient, "trucks", {
      providesTags: ["Truck"],
    }),
    governmentTrucks: createRpcQuery(build, govDashClient, "governmentTrucks", {
      providesTags: ["Truck"],
    }),
    // Fetches every page so callers get the complete set of shifts in the range.
    driverOnShifts: build.query<ConnectProto.DriverOnShift[], DriverOnShiftsArg>({
      async queryFn(arg, _api, _extra, baseQuery) {
        const shifts: ConnectProto.DriverOnShift[] = [];
        for (let page = 0; ; page++) {
          const res = await baseQuery({
            client: govDashClient,
            method: "driverOnShifts",
            args: {
              ...arg,
              listFilter: { currentPage: page, pageSize: DRIVER_ON_SHIFTS_PAGE_SIZE },
            },
          });
          if (res.error) return { error: res.error };
          const data = res.data as DriverOnShiftList;
          const batch = data.driverOnShifts ?? [];
          shifts.push(...batch);
          // The backend does not fill listInfo for this RPC yet, so a short page is the
          // end signal; the total only cuts the loop short when it is actually sent.
          const total = data.listInfo ? Number(data.listInfo.totalItems) : Infinity;
          if (batch.length < DRIVER_ON_SHIFTS_PAGE_SIZE || shifts.length >= total) break;
        }
        return { data: shifts };
      },
      providesTags: ["DriverOnShift"],
    }),
    getGovDailySummaries: createRpcQuery(build, govDashClient, "getGovDailySummaries"),
    // Get specific entities by ID
    serviceProviderById: createRpcQuery(build, govDashClient, "getServiceProviderStatsById", {
      providesTags: ["ServiceProvider"],
    }),
    pickupLocationById: createRpcQuery(build, govDashClient, "getPickupLocationStatsById", {
      providesTags: ["PickupLocation"],
    }),
    suspiciousLocations: createRpcQuery(build, govDashClient, "suspiciousLocations", {
      providesTags: ["SuspiciousLocation"],
    }),
    createSuspiciousLocation: createRpcMutation(build, govDashClient, "createSuspiciousLocation"),
    updateSuspiciousLocation: createRpcMutation(build, govDashClient, "updateSuspiciousLocation", {
      invalidatesTags: ["SuspiciousLocation"],
    }),
    archiveSuspiciousLocation: createRpcMutation(
      build,
      govDashClient,
      "archiveSuspiciousLocation",
      {
        invalidatesTags: ["SuspiciousLocation", "SuspiciousLocationAnalytics"],
      },
    ),
    unarchiveSuspiciousLocation: createRpcMutation(
      build,
      govDashClient,
      "unarchiveSuspiciousLocation",
      {
        invalidatesTags: ["SuspiciousLocation", "SuspiciousLocationAnalytics"],
      },
    ),
    getGovSuspiciousLocationAnalytics: createRpcQuery(
      build,
      govDashClient,
      "getGovSuspiciousLocationAnalytics",
      {
        providesTags: ["SuspiciousLocationAnalytics"],
      },
    ),
    serviceReceiverById: createRpcQuery(build, govDashClient, "getServiceReceiverStatsById", {
      providesTags: ["ServiceReceiver"],
    }),
    truckById: createRpcQuery(build, govDashClient, "getTruckStatsById", {
      providesTags: ["Truck"],
    }),
    jobs: createRpcQuery(build, govDashClient, "jobs", {
      providesTags: ["Job"],
    }),
    routes: createRpcQuery(build, govDashClient, "routes"),
    getJobById: createRpcQuery(build, govDashClient, "getJobById", {
      providesTags: ["Job"],
    }),
    getGovDashboardAnalytics: createRpcQuery(build, govDashClient, "getGovDashboardAnalytics", {
      providesTags: ["DashboardAnalytics"],
    }),
    depotById: createRpcQuery(build, govDashClient, "getDepotById", {
      providesTags: ["Depot"],
    }),
    disposalSiteById: createRpcQuery(build, govDashClient, "getDisposalSiteById", {
      providesTags: ["DisposalSite"],
    }),
    exportJobsReport: createRpcQuery(build, govDashClient, "exportJobsReport", {
      providesTags: ["Job"],
    }),
    exportPickupLocationsReport: createRpcQuery(
      build,
      govDashClient,
      "exportPickupLocationsReport",
      {
        providesTags: ["PickupLocation"],
      },
    ),
    serviceProviderAnalyticsById: createRpcQuery(
      build,
      govDashClient,
      "getGovServiceProviderAnalytics",
      {
        providesTags: ["GovDashboardAnalytics"],
      },
    ),
    serviceReceiverAnalyticsById: createRpcQuery(
      build,
      govDashClient,
      "getGovServiceReceiverAnalytics",
      {
        providesTags: ["GovDashboardAnalytics"],
      },
    ),
    pickupLocationAnalyticsById: createRpcQuery(
      build,
      govDashClient,
      "getGovPickupLocationAnalytics",
      {
        providesTags: ["GovDashboardAnalytics"],
      },
    ),
    truckAnalyticsById: createRpcQuery(build, govDashClient, "getGovTruckAnalytics", {
      providesTags: ["GovDashboardAnalytics"],
    }),
    depotAnalyticsById: createRpcQuery(build, govDashClient, "getGovDepotAnalytics", {
      providesTags: ["GovDashboardAnalytics"],
    }),
    getGovDisposalVisits: createRpcQuery(build, govDashClient, "getGovDisposalVisits", {
      providesTags: ["GovDashboardAnalytics"],
    }),
    getDisposalSiteAnalytics: createRpcQuery(build, govDashClient, "getGovRawScaleAnalytics", {
      providesTags: ["GovDashboardAnalytics"],
    }),
    getGovRawScaleRecordList: createRpcQuery(build, govDashClient, "getGovRawScaleRecordList", {
      providesTags: ["GovDashboardAnalytics"],
    }),
    getGovTruckTravelPath: createRpcQuery(build, govDashClient, "getGovTruckTravelPath", {
      providesTags: ["GovDashboardAnalytics"],
    }),
    truckGpsStops: createRpcQuery(build, govDashClient, "truckGpsStops"),
    getGovRawScaleRecordByCompany: createRpcQuery(
      build,
      govDashClient,
      "getGovRawScaleRecordByCompany",
      {
        providesTags: ["GovDashboardAnalytics"],
      },
    ),
    getGovAnalyticsDisposalVisitsForAll: createRpcQuery(
      build,
      govDashClient,
      "getGovAnalyticsDisposalVisitsForAll",
      {
        providesTags: ["GovDashboardAnalytics"],
      },
    ),
    getGovAnalyticsLocationVisitsForAll: createRpcQuery(
      build,
      govDashClient,
      "getGovAnalyticsLocationVisitsForAll",
      {
        providesTags: ["GovDashboardAnalytics"],
      },
    ),
    getGovAnalyticsAnomaliesForAll: createRpcQuery(
      build,
      govDashClient,
      "getGovAnalyticsAnomaliesForAll",
      {
        providesTags: ["GovDashboardAnalytics"],
      },
    ),
    getAiVerificationByJobId: createRpcQuery(build, govDashClient, "getAiVerificationByJobId", {
      providesTags: ["Job"],
    }),
    listAiVerifications: createRpcQuery(build, govDashClient, "listAiVerifications", {
      providesTags: ["Job"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useAnomalieRouteQuery,
  useAnomaliesListQuery,
  useContainerTypesQuery,
  useDepotsQuery,
  useDisposalSitesQuery,
  useDisposalSiteLocationsQuery,
  useGetGovGeoAccessQuery,
  usePickupLocationsQuery,
  useServiceProvidersQuery,
  useServiceReceiversQuery,
  useTrucksQuery,
  useGovernmentTrucksQuery,
  useDriverOnShiftsQuery,
  useGetGovDailySummariesQuery,
  useServiceProviderByIdQuery,
  usePickupLocationByIdQuery,
  useSuspiciousLocationsQuery,
  useCreateSuspiciousLocationMutation,
  useUpdateSuspiciousLocationMutation,
  useArchiveSuspiciousLocationMutation,
  useUnarchiveSuspiciousLocationMutation,
  useGetGovSuspiciousLocationAnalyticsQuery,
  useServiceReceiverByIdQuery,
  useTruckByIdQuery,
  useJobsQuery,
  useRoutesQuery,
  useGetJobByIdQuery,
  useGetGovDashboardAnalyticsQuery,
  useDepotByIdQuery,
  useDisposalSiteByIdQuery,
  useExportJobsReportQuery,
  useLazyExportJobsReportQuery,
  useLazyExportPickupLocationsReportQuery,
  useServiceProviderAnalyticsByIdQuery,
  useServiceReceiverAnalyticsByIdQuery,
  usePickupLocationAnalyticsByIdQuery,
  useTruckAnalyticsByIdQuery,
  useDepotAnalyticsByIdQuery,
  useLazyGetGovDisposalVisitsQuery,
  useGetDisposalSiteAnalyticsQuery,
  useGetGovRawScaleRecordListQuery,
  useGetGovTruckTravelPathQuery,
  useTruckGpsStopsQuery,
  useGetGovRawScaleRecordByCompanyQuery,
  useGetGovAnalyticsDisposalVisitsForAllQuery,
  useGetGovAnalyticsLocationVisitsForAllQuery,
  useGetGovAnalyticsAnomaliesForAllQuery,
  useGetAiVerificationByJobIdQuery,
  useListAiVerificationsQuery,
} = govDashApi;
